/**
 * Handle the panel's structure.
 *      ->
 * Handles the panel's state
 *      The state consist of:
 *      -> Options that the panel will use, including components settings, endpoints
 *      -> Overall model of the panel
 *      -> Event -> Action mapping
 *      -> Templates
 *      -> Current step, should the panel has progression view
 */
(function () {
    'use strict';

    angular
        .module('AsyncPanel')
        .factory('PanelManager', PanelManager);

    const STRUCTURE_URL_PATH = 'async.structure.url';
    const MODEL_URL_PATH = 'async.model.url';

    function PanelManager(lodash, ActionManager, EventsManager, SodLogger, $q, REFRESH_CONST, $timeout) {
        const panels = [];
        class Panel {
            constructor(panelScopeRef, options, externalData) {
                // options should be in this format
                // {
                //     model, <- optional, model/state that the panel should refer to for initial load
                //     structure, <- optional, structure that the panel adheres to for initial load
                //     async: {
                //         model, <- api info to make restful call to get model. optional. Used when model is absent, or when refresh is made
                //         structure <- api info to get structure info. Required when structure is absent. Used when structure is absent or when refresh is made.
                //     },
                //     panelId <- id of panel
                // };
                this.options = options;
                this.panelScope = panelScopeRef;
                this.currentScope = null;
                this.initState(externalData);
                this.getModel = function() {
                    return lodash.get(this.state, 'model', {});
                }
            }
            run() {
                this.handleRefresh(this._determineInitRefreshOptions());
            }
            initState(externalData) {
                this.state = { // default state;
                    async: null,
                    structure: null,
                    model: this._mapExternalDataToModel(externalData),
                    panelOptions: {},
                    panelId: null,
                    panelContentId: null,
                };
                this.setState('panelId', lodash.get(this.options, 'panel_id', (new Date()).getTime()));
                this.setState('panelContentId', lodash.get(this.options, 'panel_content_id', null));
                this.setState('async', lodash.get(this.options, 'async', null));
                if (lodash.has(this.options, 'structure')) {
                    this.setState('structure', angular.copy(this.options.structure));
                }
                if (lodash.has(this.options, 'model')) {
                    this.setState('model', angular.copy(this.options.model));
                }
                if (lodash.has(this.options, 'options')) {
                    this.setState('panelOptions', angular.copy(this.options.options));
                }
            }
            _mapExternalDataToModel(externalData) {
                if (!lodash.isEmpty(externalData)) {
                    return externalData;
                }
                return {};
            }
            _determineInitRefreshOptions() {
                let refreshOptions = REFRESH_CONST.REFRESH_ALL;
                const currentState = this.getState('structure');
                if (currentState !== null) {
                    if (lodash.has(this.state, MODEL_URL_PATH)) {
                        refreshOptions = REFRESH_CONST.REFRESH_MODEL;
                    }
                    if (!lodash.has(this.state, MODEL_URL_PATH)) {
                        refreshOptions = REFRESH_CONST.REFRESH_NONE;
                    }
                    this.initRender = REFRESH_CONST.REFRESH_INIT;
                    this.render();
                }
                if (currentState === null) {
                    refreshOptions = this._determineRefreshOptions();
                }
                return refreshOptions;
            }
            _determineRefreshOptions() {
                let refreshOptions = REFRESH_CONST.REFRESH_ALL;
                if (!lodash.has(this.state, STRUCTURE_URL_PATH) && !lodash.has(this.state, MODEL_URL_PATH)) {
                    refreshOptions = REFRESH_CONST.REFRESH_NONE;
                }
                if (lodash.has(this.state, STRUCTURE_URL_PATH) && !lodash.has(this.state, MODEL_URL_PATH)) {
                    refreshOptions = REFRESH_CONST.REFRESH_STRUCTURE;
                }
                if (!lodash.has(this.state, STRUCTURE_URL_PATH) && lodash.has(this.state, MODEL_URL_PATH)) {
                    refreshOptions = REFRESH_CONST.REFRESH_MODEL;
                }
                return refreshOptions;
            }
            isLoading() {
                return this.loading;
            }
            getId() {
                return this.state.panelId;
            }
            getPanelContentId() {
                return this.state.panelContentId;
            }
            getPanelScope() {
                return this.panelScope;
            }
            setState(key, value) {
                lodash.set(this.state, key, value);
            }
            syncState(external) {
                external.panelState = this.state;
            }
            getState(key, _default) {
                if (!_default) _default = false;
                return lodash.get(this.state, key, _default);
            }
            setOptions(data, externalModel = {}) {
                this.options = data;
                this.initState(externalModel);
            }
            updateModel(resData) {
                const storeType = lodash.get(this.state.async.model, 'storeType', false);
                switch (storeType) {
                    case "merge": {
                        angular.merge(this.state.model, resData);
                        break;
                    }
                    case "extend": {
                        angular.extend(this.state.model, resData);
                        break;
                    }
                    default:
                        this.setState('model', resData);
                        break;
                }

            }
            refresh(showLoading = false) {
                this.handleRefresh(this._determineRefreshOptions(), showLoading);
            }
            refreshModel(showLoading = false) {
                this.handleRefresh(REFRESH_CONST.REFRESH_MODEL, showLoading);
            }
            refreshStructure(showLoading = false) {
                this.handleRefresh(REFRESH_CONST.REFRESH_STRUCTURE, showLoading);
            }

            handleRefresh(refreshOptions, showLoading = false) {
                // fix empty panel to not run handleRefresh
                if (lodash.get(this, 'panelScope.vm') === undefined) return;
                if (showLoading) {
                    this.state.structure.template = '<div class="async-panel-loading" ng-if="!vm.options.settings.hide_loading"></div>';
                    this.render();
                }

                this.loading = true;
                const _promises = [];

                if ([REFRESH_CONST.REFRESH_ALL, REFRESH_CONST.REFRESH_STRUCTURE].indexOf(refreshOptions) >= 0) {
                    EventsManager.destroy(this.getId());

                    if (lodash.has(this.state.async, 'structure.event.loading')) {
                        this.panelScope.$emit(this.state.async.structure.event.loading);
                    }

                    if (this.compileElementRef) this.compileElementRef.off();
                    // _promises.push(ActionManager('test-get-structure', this.getId()));
                    _promises.push(ActionManager('get-structure', this.getId(), this.state.async.structure, this.state.model));
                }
                if ([REFRESH_CONST.REFRESH_ALL, REFRESH_CONST.REFRESH_MODEL].indexOf(refreshOptions) >= 0) {
                    // _promises.push(ActionManager('test-get-model'));
                    _promises.push(ActionManager('get-model', this.state.async.model, this.state.model));
                }

                $q.all(_promises).then(responses=> { // On Success
                    SodLogger.info('Responses', responses[0]);

                    switch (refreshOptions) {
                        case REFRESH_CONST.REFRESH_ALL:
                            this.setState('structure', responses[0]);
                            this.updateModel(responses[1].data || responses[1].rows);
                            break;
                        case REFRESH_CONST.REFRESH_STRUCTURE:
                            this.setState('structure', responses[0]);
                            break;
                        case REFRESH_CONST.REFRESH_MODEL:
                            this.updateModel(responses[0].data || responses[0].rows);
                            break;
                        default: break;
                    }

                    // bind events
                    if ([REFRESH_CONST.REFRESH_ALL, REFRESH_CONST.REFRESH_STRUCTURE].indexOf(refreshOptions) >= 0) {
                        EventsManager.listen(this, this.getState('structure.events', []));
                        if (this.initRender && this.initRender === REFRESH_CONST.REFRESH_INIT) {
                            this.initRender = REFRESH_CONST.REFRESH_INIT_DONE;
                            this.destroyCompileElement();
                        }

                        if (lodash.has(this.state.async, 'structure.event.loaded')) {
                            // $timeout(function() {
                                var structure_event = lodash.find(this.getState('structure.events'), {event: this.state.async.structure.event.loaded});
                                if (structure_event)
                                    EventsManager.trigger(this, structure_event);
                            // }.bind(this));
                        }
                    }
                    if ([REFRESH_CONST.REFRESH_ALL, REFRESH_CONST.REFRESH_MODEL].indexOf(refreshOptions) >= 0) {
                        if (lodash.has(this.state.async, 'model.event.loaded')) {
                            this.panelScope.$emit(this.state.async.model.event.loaded);
                        }
                    }
                    this.render();
                    this.loading = false;
                }, (...args)=> { // On Error
                    SodLogger.error('Panel Refresh has Error', ...args);
                });
            }
            render() {
                // console.log('render',this.panelScope);
                // fix empty panel to not run handleRefresh
                if (lodash.get(this, 'panelScope.vm') === undefined) return;
                this.panelScope.vm.handleRender();
            }
            setCompileElement(ref) {
                this.compileElementRef = ref;
            }
            destroyCompileElement() {
                if (this.compileElementRef) {
                    this.compileElementRef.off();
                }
            }
            destroyPanelScope() {
                if (this.panelScope !== null) {
                    this.panelScope.$destroy();
                    this.panelScope = null;
                }
            }
            setCurrentScope(_scope) {
                this.destroyCurrentScope();
                this.currentScope = _scope;
            }
            getCurrentScope() {
                return this.currentScope;
            }
            destroyCurrentScope() {
                if (this.currentScope !== null) {
                    this.currentScope.$destroy();
                    this.currentScope = null;
                }
            }
            destroy() {
                // destroy all events
                this.destroyCompileElement();
                this.destroyCurrentScope();
                this.destroyPanelScope();
                EventsManager.destroy(this.getId());
            }
            emitEvent(eventName, event_data) {
                this.panelScope.$emit(eventName, event_data);
            }
            getModel() {
                return this.state.model;
            }
            updateFormModel() {
                if (typeof this.state.setModel === typeof function() {}) {
                    this.state.setModel(this.state.model);
                }
            }
        }

        return {
            init: function(panelScopeRef, panelSettings, externalData) {
                const panel = new Panel(panelScopeRef, panelSettings, externalData);
                const panelIndex = lodash.findIndex(panels, function(_p) {
	                return _p.getId() === panel.getId();
                });
                if (panelIndex >= 0)
                {
	                panels[panelIndex] = panel;
                } else {
	                panels.push(panel);
                }

                return panel;
            },
            get: function(id) {
                return lodash.find(panels, (panel) => panel.getId() == id);
            },
            getByPanelContentId: function(panelContentId) {
                return lodash.find(panels, (panel) => panel.getPanelContentId() == panelContentId);
            },
            getAll: function() {
                return panels;
            },
            refreshAll: function() {
                panels.map(function(panel) {
                   panel.refresh();
                });
            },
            destroy: destroy,
            destroyAll: destroyAll
        };

        function destroy(id) {
            const index = lodash.findIndex(panels, (panel) => panel.getId() == id);
            const panel = panels[index];
            if (panel) {
                panel.destroy();
                panels.splice(index, 1);
            }
            return $q.when(true);
        }

        function destroyAll() {
            return $q.when(panels.map(function(panel) {
                return destroy(panel.getId());
            }));
        }
    }
})();